import React, {useEffect, useRef, useState} from 'react';
import cl from "./FormLinkedItemListSelect.module.css";
import {Autocomplete, Button, Chip, CircularProgress, FormHelperText, TextField} from "@mui/material";
import ServerManager from "../API/ServerManager";
import FormControlCaption from "./FormControlCaption";

const ITEMS_PER_RQ_PAGE = 30;
const TYPING_TIMEOUT = 500; // [ms]

const FormLinkedItemListSelect = ({
                                        label,
                                        initSelectedIds,
                                        initRefList,
                                        apiName2,
                                        inputWidthLimit,
                                        controlRef,
                                        selectedItemsAreaWidthLimit,
                                        selectedItemsAreaType = "normal", // normal|column
                                        onChange,
                                        showIds,
                                        renderItemExtraInfoFunc,
                                        renderInitialItemExtraInfoFunc,
                                        itemNameFunc,
                                        filterExtraParams = {},
                                        dataRefs,
                                        tipText,
                                        readOnly,
                                    }) => {

    // NOTE: исходную выборку сортируем по алфавиту. однако, при добавлении, новые элементы всегда будут вставляться в конец списка
    const [selectedOptions, setSelectedOptions] = useState(() => {
        return initSelectedIds.map(id => ({
            id: id,
            name: getInitialOptionName(id, initRefList, itemNameFunc),
            isInitial: true, // TODO? no need?
        })).sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    })

    const loadingOption = {id: -1, name: "loading", isLoading: true}
    const hintOption = {id: -2, name: "hint", isHint: true}
    const errorOption = {id: -3, name: "error", isError: true}

    const [mainOptions, setMainOptions] = useState([]);

    const [errorMessage, setErrorMessage] = useState("");

    const rqIdCounter = useRef(0);
    const [status, setStatus] = useState("closed"); // "", closed, idle, mainLoad, mainError, typing
    const [mainErrorMessage, setMainErrorMessage] = useState(""); // TODO!!
    const [lastTypingTime, setLastTypingTime] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    const [ownDataRefs, setOwnDataRefs] = useState(dataRefs);

    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                getSelectedIds: () => selectedOptions.map(x => x.id),
                getIsEmpty: () => selectedOptions.length === 0,
                validateNotEmpty: validateNotEmpty,
                setErrorMessage: setErrorMessage,
            };
        }
    })

    function validateNotEmpty(errorMessage) {
        if (selectedOptions.length > 0)
            return true;
        setErrorMessage(errorMessage);
        return false;
    }

    function handleSetSelectedOptions(value) {
        setSelectedOptions(value);
        if (onChange)
            onChange(value.map(x => x.id));
    }

    const [autocompleteOpen, setAutocompleteOpen] = useState(false);

    const correctedInputWidthLimit = inputWidthLimit === undefined ? 10000 : inputWidthLimit;
    const correctedSelectedItemsAreaWidthLimit = selectedItemsAreaWidthLimit === undefined ? 10000 : selectedItemsAreaWidthLimit;

    function filterOptions(options) {
        if (status === "mainLoad" || status === "typing")
            return [loadingOption];

        return options;
    }

    function getOptionLabel(option) {
        if (selectedOptions.some(x => x.id === option.id))
            return `${option.name} (выбрано)`;

        return option.name;
    }

    function getOptionKey(option) {
        return option.id;
    }

    function isOptionEqualToValue(option, value) {
        return option.id === value.id;
    }

    function onFilterEdit(forceUpdate = false) {
        setStatus("typing");
        setLastTypingTime(forceUpdate ? 0 : Date.now());
        setMainOptions([]);
        rqIdCounter.current++;
    }

    async function requestMainItems() {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("mainLoad")
        try {
            //await new Promise(resolve => setTimeout(resolve, 1000)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList(apiName2, ITEMS_PER_RQ_PAGE, searchQuery, "name", "asc", 0, filterExtraParams);
            if (rqIdCounter.current === rqId) {
                const responseList = data[apiName2];
                // TODO? кешировать ответ, если общее кол-во опций мало? (чтобы не переспрашивать одно и то же)

                if (itemNameFunc) {
                    for (const item of responseList)
                        item.name = itemNameFunc(item);
                }

                if (!data.pagination.is_last)
                    responseList.push(hintOption);

                setMainOptions(responseList);
                setOwnDataRefs(data.refs);
                setStatus("idle");
            } else {
                console.log("rq answer ignored!");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                setMainErrorMessage(`Ошибка при загрузке данных: ${error}`);
                setStatus("mainError");
                setMainOptions([errorOption]);
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    // запуск/продление временной задержки во время ввода текста
    useEffect(() => {
        async function wait() {
            const leftTime = lastTypingTime + TYPING_TIMEOUT - Date.now();
            if (leftTime > 0)
                await new Promise(resolve => setTimeout(resolve, leftTime));
            if (!ignore)
                requestMainItems();
        }

        if (status !== "typing")
            return;

        let ignore = false;
        wait();
        return () => {
            ignore = true;
        }

    }, [status, lastTypingTime]);

    function renderSelectedItem(item, index) {
        return <Chip
            key={index}
            label={<div style={{display: "flex", gap: "5px", alignItems: "center"}}>
                <span style={{whiteSpace: "wrap"}}>{item.name}</span>
                {item.isInitial && renderInitialItemExtraInfoFunc && renderInitialItemExtraInfoFunc(item, cl.extraInfo)}
                {!item.isInitial && renderItemExtraInfoFunc && renderItemExtraInfoFunc(item, cl.extraInfo, ownDataRefs)}
                {showIds && renderIdInfo(item.id)}
            </div>}
            variant="outlined"
            sx={{
                backgroundColor: "#d0d0d0",
                fontSize: "16px",
                height: "min-content",
                minHeight: "32px",
                paddingTop: "4px",
                paddingBottom: "4px"
            }}
            onDelete={readOnly ? undefined : () => {
                handleSetSelectedOptions(selectedOptions.filter(x => x.id !== item.id));
            }}
        />
    }

    function renderButton(label, onClick, sx) {
        return <Button variant="outlined" size="small" onClick={onClick} sx={sx}>{label}</Button>
    }

    return (
        <div className={cl.main}>
            <FormControlCaption
                text={label}
                tipText={tipText}
            />
            <div
                className={selectedItemsAreaType === "column" ? cl.selectedItemsArea_column : cl.selectedItemsArea_normal}
                style={{maxWidth: `min(${correctedSelectedItemsAreaWidthLimit}px, 100%)`}}>
                {selectedOptions.length > 0 ?
                    selectedOptions.map((item, index) => renderSelectedItem(item, index)) :
                    <span className={`${cl.emptyListLabel} ${errorMessage ? cl.error : ""}`}>(список пуст)</span>}
            </div>
            {autocompleteOpen ?
                <Autocomplete
                    open
                    renderInput={renderAutocompleteInput}
                    options={mainOptions}
                    sx={{width: `min(${correctedInputWidthLimit}px, 100%)`}}
                    disableClearable
                    autoHighlight /* при вводе текста сразу подсвечивается первый из доступных вариантов, что позволяет сразу клавишей Enter завершить выбор */
                    blurOnSelect /* после выбора, компонент теряет фокус */
                    filterOptions={filterOptions}
                    noOptionsText="Варианты не найдены"
                    getOptionLabel={getOptionLabel}
                    clearText="Очистить"
                    openText="Открыть список"
                    closeText="Закрыть список"
                    /*value={null}*/
                    getOptionKey={getOptionKey}
                    isOptionEqualToValue={isOptionEqualToValue}
                    getOptionDisabled={option => option.id < 0 || selectedOptions.some(x => x.id === option.id)}
                    onChange={(event, value) => {
                        if (value) {
                            handleSetSelectedOptions([...selectedOptions, value]);
                            setAutocompleteOpen(false);
                        }
                    }}
                    renderOption={(props2, option) => {
                        const {key, ...otherProps} = props2;

                        if (option.isHint) {
                            return (
                                <li key={key} {...otherProps}
                                    style={{
                                        display: "block",
                                        fontSize: "14px",
                                        color: "#844",
                                        fontStyle: "italic",
                                        opacity: "0.8"
                                    }}>
                                    <p style={{marginBottom: "-5px"}}>Показаны не все варианты!</p>
                                    <p>Уточните поисковый запрос</p>
                                </li>
                            );
                        }

                        if (option.isLoading) {
                            return (
                                <li key={key} {...otherProps}
                                    style={{display: "flex", gap: "10px", color: "#000", opacity: "0.6"}}>
                                    <CircularProgress color="inherit" size={20}/>
                                    Поиск вариантов...
                                </li>
                            );
                        }

                        if (option.isError) {
                            return (
                                <li key={key} {...otherProps}
                                    style={{
                                        display: "block",
                                        fontSize: "14px",
                                        color: "#844",
                                        fontStyle: "italic",
                                        opacity: "0.8"
                                    }}>
                                    <p style={{marginBottom: "-5px"}}>Не удалось запросить варианты!</p>
                                    <p>Попробуйте повторить поиск</p>
                                </li>
                            );
                        }

                        return (
                            <li key={key} {...otherProps}
                                style={{display: "flex", gap: "10px"}}>
                                {getOptionLabel(option)}
                                {renderItemExtraInfoFunc && renderItemExtraInfoFunc(option, cl.extraInfo, ownDataRefs)}
                                {showIds && option.id !== null && renderIdInfo(option.id)}
                            </li>
                        );
                    }}
                    onInputChange={(e, v, r) => {
                        //console.log("onInputChange! v=", v, "r=", r);
                        if (r === "input") {
                            setSearchQuery(v);
                            onFilterEdit();
                        }
                    }}
                    onClose={(event, reason) => {
                        setAutocompleteOpen(false);
                        rqIdCounter.current++;
                        setMainOptions([]);
                        setSearchQuery("");
                    }}
                /> :
                (readOnly ?
                    null :
                <div className={cl.buttonsArea}>
                    {renderButton("Добавить", () => {
                        setAutocompleteOpen(true);
                        if (errorMessage) {
                            setErrorMessage("");
                        }
                        requestMainItems();
                    })}
                    {selectedOptions.length > 0 && renderButton("Очистить", () => {
                        handleSetSelectedOptions([]);
                    }, {borderColor: "gray", color: "#844"})}
                </div>)}
            {errorMessage &&
                <FormHelperText error sx={{marginLeft: "14px", marginTop: "2px"}}>{errorMessage}</FormHelperText>}
        </div>
    );
};

function renderIdInfo(id) {
    return <span className={cl.idInfo}>ID {id}</span>
}

function renderAutocompleteInput(params) {
    return <TextField
        {...params}
        placeholder="введите текст для поиска"
        autoFocus
        slotProps={{
            htmlInput: {
                ...params.inputProps,
                maxLength: 255,
            },
            input: {
                ...params.InputProps,
                sx: {
                    ...params.InputProps.sx,
                    backgroundColor: "white",
                    borderRadius: "4px"
                }
            },
        }}
    />
}

// TODO? shared?
function getInitialOptionName(optionId, refList, itemNameFunc) {
    if (optionId === null)
        return "-null-";

    if (optionId === 0)
        return "-id0-";

    if (optionId < 0)
        return "-negativeId-";

    const refItem = refList?.[optionId] ?? undefined;

    if (!refItem)
        return "-refItemNotFound-";

    const name = itemNameFunc ? itemNameFunc(refItem) : refItem.name;

    if (name)
        return name;

    return "-nameNotFound-";
}

export default FormLinkedItemListSelect;